const _ = require("lodash");
const {
	findArguments,
	findOptionalArgument,
	parseColor,
} = require("../util/ParserHelpers");
const ImageCache = require("../../util/ImageCache");

const makeParser = function(settings = {}) {
	return function(ps) {
		let optArg = findOptionalArgument(ps);
		// sets defaults for optional arguments
		let altHand = false;
		let twoHands = false;
		let colorOption = false;
		let fillColor = null;
		while (optArg) {
			if (optArg.text === "alt") {
				altHand = true;
			} else if (optArg.text === "twohands") {
				twoHands = true;
			} else if (_.startsWith(optArg.text, "color=") && optArg.text !== "") {
				fillColor = parseColor(ps, optArg.text);
				colorOption = true;
				// check if entered color is not valid
				if (
					![
						"red",
						"orange",
						"yellow",
						"green",
						"teal",
						"blue",
						"royalblue",
						"purple",
						"pink",
					].includes(fillColor)
				) {
					ps.pushError(
						optArg.text +
							" is not a valid color.  red, orange, yellow, green, teal, blue, royalblue, purple, and pink are the only valid options."
					);
				}
				if (fillColor === "green") {
					colorOption = false; // green is already default
				}
			} else {
				ps.pushError(
					optArg.text +
						" is not a valid optional argument. 'color=COLOR_NAME', 'alt', and 'twohands' are the only valid options."
				);
				return false;
			}
			optArg = findOptionalArgument(ps);
		}
		const args = findArguments(ps, 1);
		if (!args) {
			return false;
		}
		const countingHand = parseInt(args[0].getRest());
		if (!_.inRange(countingHand, 0, 11)) {
			ps.pushError(
				countingHand +
					" is not a valid argument. Please enter an integer in the range from 0-10."
			);
			return false;
		}
		if (twoHands && countingHand > 5) {
			twoHands = false;
		}
		if (altHand && (countingHand === 0 || countingHand === 5)) {
			altHand = false;
		}
		return [
			{
				type: "hands",
				countingHand,
				altHand,
				twoHands,
				colorOption,
				fillColor,
			},
		];
	};
};

const handNode = {
	draw(rc, x, y, node, measure) {
		const context = rc.context;
		const {
			top,
			handWidthScaled,
			handHeight,
			totalScaledWidth,
			firstHandNumber,
			secondHandNumber,
			colorOption,
			fillColor,
		} = measure;

		// get svg images
		const hands_image = n =>
			ImageCache.src(
				global.BASE_URL +
					`assets/images/problem-art/hands/CountingHands_${n}${
						colorOption ? `_${fillColor}` : ""
					}.svg`,
				true
			);

		const topY = y - top;
		const w = totalScaledWidth;
		const h = handHeight;
		context.save();

		// get widths for each hand
		const firstHandWidth = handWidthScaled[0];
		const secondHandWidth = handWidthScaled[1];

		// draw hands
		if (node.twoHands || node.countingHand > 5) {
			// first hand shows at most 5 fingers
			// second hand shows however many leftover
			// don't add the "a" if it's 0 or 5 cause those don't have alt versions
			const firstHandA =
				node.altHand && _.inRange(firstHandNumber, 1, 5) ? "a" : "";
			const secondHandA =
				node.altHand && _.inRange(secondHandNumber, 1, 5) ? "a" : "";
			context.drawImage(
				hands_image(firstHandNumber + firstHandA),
				x,
				topY,
				firstHandWidth,
				h
			);
			context.translate(2 * x + w, 0);
			context.scale(-1, 1);
			context.drawImage(
				hands_image(secondHandNumber + secondHandA),
				x,
				topY,
				secondHandWidth,
				h
			);
		} else {
			context.drawImage(
				hands_image(
					node.countingHand +
						(node.altHand && _.inRange(node.countingHand, 1, 5) ? "a" : "")
				),
				x,
				topY,
				w,
				h
			);
		}

		context.restore();
	},
	measure(rc, fontInfo, props, node) {
		// The size of the hands depends on the font size,
		// since we want it to be affected by \bigger.
		const fontScaleFactor = fontInfo.size / props.baseFontSize;
		const handHeight = 150 * props.sizeScaleFactor * fontScaleFactor;

		// get svg widths
		const svgWidth = [
			18, // width of one hand - no fingers
			18, // width of one hand - one finger
			20, // width of one hand - two fingers
			21, // width of one hand - three fingers
			25, // width of one hand - four fingers
			31, // width of one hand - five fingers
		];

		const svgWidthAlt = [
			18, // width of one hand - zero fingers (no alt representation, but it's convenient to have here)
			24, // width of one hand - one finger (alt representation)
			25, // width of one hand - two fingers (alt representation)
			25, // width of one hand - three fingers (alt representation)
			25, // width of one hand - four fingers (alt representation)
			31, // width of one hand - five fingers (no alt representation, but it's convenient to have here)
		];

		let leftHandWidth = 0;
		let rightHandWidth = 0;
		let firstHandNumber = null;
		let secondHandNumber = null;

		// get proper svg width, depending on conditions
		if (node.countingHand <= 5) {
			firstHandNumber = node.countingHand;
			if (node.altHand) {
				leftHandWidth = svgWidthAlt[node.countingHand];
			} else {
				leftHandWidth = svgWidth[node.countingHand];
			}
			if (node.twoHands) {
				rightHandWidth = svgWidth[0];
				secondHandNumber = 0;
			}
		} else {
			const correctWidth = node.altHand ? svgWidthAlt : svgWidth;
			firstHandNumber = _.clamp(node.countingHand, 0, 5);
			secondHandNumber = node.countingHand - firstHandNumber;
			leftHandWidth = correctWidth[firstHandNumber];
			rightHandWidth =
				node.countingHand > 5 || node.twoHands
					? correctWidth[secondHandNumber]
					: 0;
		}

		const totalSvgWidth = leftHandWidth + rightHandWidth;
		const colorOption = node.colorOption;
		const fillColor = node.fillColor;

		// return final handwidth
		const widthScaleFactor = (handHeight * 3) / 4 / 31;
		const totalScaledWidth = totalSvgWidth * widthScaleFactor;
		const handWidthScaled = [
			leftHandWidth * widthScaleFactor,
			rightHandWidth * widthScaleFactor,
		];

		return {
			width: totalScaledWidth,
			top: handHeight / 2,
			bottom: handHeight / 2,
			handWidthScaled,
			handHeight,
			totalScaledWidth,
			firstHandNumber,
			secondHandNumber,
			colorOption,
			fillColor,
		};
	},
	readAloud: "",
};

module.exports = {
	parsers: {
		mathCommands: {
			hands: makeParser(),
		},
	},
	nodes: {
		hands: handNode,
	},
};
